import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, ArrowDown } from 'lucide-react';
import SectionHead from './SectionHead';
import { Reveal } from './reveal';

// Barge Lifetime Cost Calculator
//
// All numbers except the HDPE purchase-price default come from the user.
// We never invent maintenance or performance claims for competing materials.
// Results are pushed to a window-level ref (`__calcState`) and a
// `calculator-state` event so Contact.jsx can stamp hidden fields on the
// existing quote-form submission without touching visible form UI.

const HORIZONS = [5, 10, 20, 30];
const CHART_COLORS = {
  hdpe: '#67e8f9',    // cyan-300 (brighter neon)
  steel: '#fb923c',   // orange-400 (brighter neon)
  aluminum: '#e4e4e7', // zinc-200 (brighter neon)
};

const num = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const fmt$ = (n) =>
  (Math.round(n)).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function anyPositive(obj, keys) {
  return keys.some((k) => num(obj[k]) > 0);
}
const STEEL_INPUT_KEYS = ['paintCost', 'weldYr', 'anodesYr', 'haulCost', 'daysOut', 'avgDailyRev'];
const ALU_INPUT_KEYS = STEEL_INPUT_KEYS;
function isSteelComplete(s) {
  return anyPositive(s, STEEL_INPUT_KEYS);
}
function isAluminumComplete(a) {
  return anyPositive(a, ALU_INPUT_KEYS);
}

// Yearly cost stream for a material — { year: cumulativeCost }.
// `avgDailyRev * daysOut` is treated as an annual figure and applied every year.
// Purchase price is intentionally excluded — buy prices swing too much to compare
// fairly. We compare lifetime running/downtime costs only.
function stream(annual, periodics, avgDailyRev, daysOut, horizon) {
  const out = [];
  let cum = 0;
  out.push({ year: 0, cum });
  for (let y = 1; y <= horizon; y++) {
    let add = annual + avgDailyRev * daysOut;
    for (const p of periodics) {
      if (p.every > 0 && y % p.every === 0) add += p.cost;
    }
    cum += add;
    out.push({ year: y, cum });
  }
  return out;
}

const DEFAULT_STEEL = { paintCost: '', paintEvery: '5', weldYr: '', anodesYr: '', haulCost: '', haulEvery: '2', daysOut: '', avgDailyRev: '' };
const DEFAULT_ALU = { paintCost: '', paintEvery: '7', weldYr: '', anodesYr: '', haulCost: '', haulEvery: '3', daysOut: '', avgDailyRev: '' };
const DEFAULT_HDPE = { upkeepYr: '0', daysOut: '0', avgDailyRev: '' };

export default function CostCalculator() {
  const [compareSteel, setCompareSteel] = useState(true);
  const [compareAlu, setCompareAlu] = useState(true);
  const [horizon, setHorizon] = useState(10);
  const [steel, setSteel] = useState(DEFAULT_STEEL);
  const [alu, setAlu] = useState(DEFAULT_ALU);
  const [hdpe, setHdpe] = useState(DEFAULT_HDPE);
  const [firedShown, setFiredShown] = useState(false);

  // Ensure at least one competitor is selected.
  useEffect(() => {
    if (!compareSteel && !compareAlu) setCompareSteel(true);
  }, [compareSteel, compareAlu]);

  const results = useMemo(() => {
    const steelStream = isSteelComplete(steel)
      ? stream(
          num(steel.weldYr) + num(steel.anodesYr),
          [
            { cost: num(steel.paintCost), every: num(steel.paintEvery) },
            { cost: num(steel.haulCost), every: num(steel.haulEvery) },
          ],
          num(steel.avgDailyRev),
          num(steel.daysOut),
          30,
        )
      : null;
    const aluStream = isAluminumComplete(alu)
      ? stream(
          num(alu.weldYr) + num(alu.anodesYr),
          [
            { cost: num(alu.paintCost), every: num(alu.paintEvery) },
            { cost: num(alu.haulCost), every: num(alu.haulEvery) },
          ],
          num(alu.avgDailyRev),
          num(alu.daysOut),
          30,
        )
      : null;
    const hdpeStream = stream(
      num(hdpe.upkeepYr),
      [],
      num(hdpe.avgDailyRev),
      num(hdpe.daysOut),
      30,
    );

    const at = (s) => (s ? s[horizon].cum : null);
    const totalHdpe = at(hdpeStream);
    const totalSteel = compareSteel ? at(steelStream) : null;
    const totalAlu = compareAlu ? at(aluStream) : null;

    // Break-even year: first year where HDPE cumulative <= competitor cumulative.
    const breakeven = (competitor) => {
      if (!competitor) return null;
      for (let y = 0; y <= 30; y++) {
        if (hdpeStream[y].cum <= competitor[y].cum) return y === 0 ? 'immediate' : y;
      }
      return 'none';
    };

    const revLossHdpe = num(hdpe.avgDailyRev) * num(hdpe.daysOut) * horizon;
    const revLossSteel = compareSteel && steelStream ? num(steel.avgDailyRev) * num(steel.daysOut) * horizon : null;
    const revLossAlu = compareAlu && aluStream ? num(alu.avgDailyRev) * num(alu.daysOut) * horizon : null;

    // Service days over the horizon for each material.
    const daysHdpe = num(hdpe.daysOut) * horizon;
    const daysSteel = compareSteel && steelStream ? num(steel.daysOut) * horizon : null;
    const daysAlu = compareAlu && aluStream ? num(alu.daysOut) * horizon : null;

    // Downtime callout: pick the competitor with the most days-saved vs HDPE.
    // Attach yearly $ savings vs that same competitor for the annual impact line.
    let downtimeCallout = null;
    const candidates = [];
    if (daysSteel != null && daysSteel - daysHdpe > 0) {
      const yearlySavings = totalSteel != null ? (totalSteel - totalHdpe) / horizon : 0;
      candidates.push({ label: 'steel', days: daysSteel - daysHdpe, yearlySavings });
    }
    if (daysAlu != null && daysAlu - daysHdpe > 0) {
      const yearlySavings = totalAlu != null ? (totalAlu - totalHdpe) / horizon : 0;
      candidates.push({ label: 'aluminum', days: daysAlu - daysHdpe, yearlySavings });
    }
    if (candidates.length) {
      candidates.sort((a, b) => b.days - a.days);
      downtimeCallout = candidates[0];
    }

    return {
      hdpeStream, steelStream, aluStream,
      totalHdpe, totalSteel, totalAlu,
      revLossHdpe, revLossSteel, revLossAlu,
      daysHdpe, daysSteel, daysAlu,
      downtimeCallout,
      beVsSteel: compareSteel && steelStream ? breakeven(steelStream) : null,
      beVsAlu: compareAlu && aluStream ? breakeven(aluStream) : null,
    };
  }, [steel, alu, hdpe, compareSteel, compareAlu, horizon]);

  // Push state to the contact form (hidden fields) + fire dataLayer event.
  useEffect(() => {
    const materialsUsed = [];
    if (compareSteel && results.totalSteel != null) materialsUsed.push('steel');
    if (compareAlu && results.totalAlu != null) materialsUsed.push('aluminum');
    const calcUsed = materialsUsed.length > 0;

    const payload = {
      calc_used: calcUsed ? 'true' : 'false',
      calc_materials_compared: materialsUsed.join(','),
      calc_time_horizon_years: calcUsed ? String(horizon) : '',
      calc_total_hdpe: calcUsed ? String(Math.round(results.totalHdpe)) : '',
      calc_total_steel: compareSteel && results.totalSteel != null ? String(Math.round(results.totalSteel)) : '',
      calc_total_aluminum: compareAlu && results.totalAlu != null ? String(Math.round(results.totalAlu)) : '',
      calc_breakeven_vs_steel: compareSteel && results.beVsSteel != null ? String(results.beVsSteel) : '',
      calc_breakeven_vs_aluminum: compareAlu && results.beVsAlu != null ? String(results.beVsAlu) : '',
      calc_revloss_hdpe: calcUsed ? String(Math.round(results.revLossHdpe)) : '',
      calc_revloss_steel: compareSteel && results.revLossSteel != null ? String(Math.round(results.revLossSteel)) : '',
      calc_revloss_aluminum: compareAlu && results.revLossAlu != null ? String(Math.round(results.revLossAlu)) : '',
    };
    if (typeof window !== 'undefined') {
      window.__calcState = payload;
      window.dispatchEvent(new CustomEvent('calculator-state', { detail: payload }));
    }
    if (calcUsed && !firedShown && typeof window !== 'undefined') {
      (window.dataLayer = window.dataLayer || []).push({
        event: 'calculator_used',
        materials: materialsUsed.join(','),
      });
      setFiredShown(true);
    }
  }, [results, compareSteel, compareAlu, horizon, firedShown]);

  const jumpToQuote = () => {
    if (typeof window !== 'undefined') {
      (window.dataLayer = window.dataLayer || []).push({ event: 'calculator_quote_click' });
    }
    const el = document.getElementById('quote-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="cost-calculator" className="relative py-16 sm:py-20 lg:py-28 bg-black border-t border-white/5 scroll-mt-24">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead
          eyebrow="Barge Ownership Math"
          titleLines={[<>Lifetime Cost <span key="cyan" className="text-cyan-300">Calculator</span></>]}
          body="Enter what a steel or aluminum hull actually costs you to run and we'll show total ownership cost against HDPE over 5–30 years."
        />

        <Reveal delay={0.15}>
          <div className="mt-10 panel rounded-xl p-5 sm:p-7">
            {/* Comparison + horizon */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4 pb-5 border-b border-white/10">
              <div className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold w-full sm:w-auto">Compare HDPE against:</div>
              <div className="flex items-center gap-6">
                <Check label="Steel" checked={compareSteel} onChange={setCompareSteel} testId="calc-toggle-steel" />
                <Check label="Aluminum" checked={compareAlu} onChange={setCompareAlu} testId="calc-toggle-aluminum" />
              </div>
              <div className="ml-auto flex items-center gap-3">
                <div className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Time horizon</div>
                <div className="flex bg-black/60 border border-white/10 rounded-md overflow-hidden">
                  {HORIZONS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setHorizon(h)}
                      data-testid={`calc-horizon-${h}`}
                      className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] transition-colors ${
                        h === horizon ? 'bg-cyan-500/20 text-cyan-200' : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      {h} yr
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Input grid */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <MaterialPanel
                title="HDPE Barge" tone="cyan" testId="calc-hdpe"
                fields={[
                  ['upkeepYr', 'Hardware & fittings upkeep / yr', "HDPE hulls don't rust, need painting, or require blasting. Enter any expected hardware upkeep.", '$'],
                  ['daysOut', 'Days out of service / yr', null, ''],
                  ['avgDailyRev', 'Average daily revenue', null, '$'],
                ]}
                state={hdpe} setState={setHdpe}
              />
              {compareSteel && (
                <MaterialPanel
                  title="Steel Barge" tone="orange" testId="calc-steel"
                  fields={[
                    ['paintCost', 'Blasting / painting cost', null, '$'],
                    ['paintEvery', 'Blast / paint every N years', null, ''],
                    ['weldYr', 'Welding / plate repair / yr', null, '$'],
                    ['anodesYr', 'Zincs / anodes / yr', null, '$'],
                    ['haulCost', 'Haul-out cost', null, '$'],
                    ['haulEvery', 'Haul-out every N years', null, ''],
                    ['daysOut', 'Days out of service / yr', null, ''],
                    ['avgDailyRev', 'Average daily revenue', null, '$'],
                  ]}
                  state={steel} setState={setSteel}
                />
              )}
              {compareAlu && (
                <MaterialPanel
                  title="Aluminum Barge" tone="zinc" testId="calc-aluminum"
                  fields={[
                    ['paintCost', 'Bottom coating cost', null, '$'],
                    ['paintEvery', 'Paint every N years', null, ''],
                    ['weldYr', 'Weld crack / structural repair / yr', null, '$'],
                    ['anodesYr', 'Anodes / galvanic protection / yr', null, '$'],
                    ['haulCost', 'Haul-out cost', null, '$'],
                    ['haulEvery', 'Haul-out every N years', null, ''],
                    ['daysOut', 'Days out of service / yr', null, ''],
                    ['avgDailyRev', 'Average daily revenue', null, '$'],
                  ]}
                  state={alu} setState={setAlu}
                />
              )}
            </div>

            {/* Results */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ResultCard
                label="HDPE Barge" tone="cyan"
                total={results.totalHdpe} horizon={horizon}
                daysOut={results.daysHdpe}
                revLoss={results.revLossHdpe}
                lowest={isLowest(results.totalHdpe, [results.totalSteel, results.totalAlu])}
                testId="calc-result-hdpe"
              />
              {compareSteel && (
                <ResultCard
                  label="Steel Barge" tone="orange"
                  total={results.totalSteel} horizon={horizon}
                  daysOut={results.daysSteel}
                  revLoss={results.revLossSteel}
                  incomplete={!isSteelComplete(steel)}
                  lowest={isLowest(results.totalSteel, [results.totalHdpe, results.totalAlu])}
                  testId="calc-result-steel"
                />
              )}
              {compareAlu && (
                <ResultCard
                  label="Aluminum Barge" tone="zinc"
                  total={results.totalAlu} horizon={horizon}
                  daysOut={results.daysAlu}
                  revLoss={results.revLossAlu}
                  incomplete={!isAluminumComplete(alu)}
                  lowest={isLowest(results.totalAlu, [results.totalHdpe, results.totalSteel])}
                  testId="calc-result-aluminum"
                />
              )}
            </div>

            {/* Deltas */}
            <div className="mt-5 space-y-1.5 text-sm">
              {compareSteel && results.totalSteel != null && (
                <Delta hdpe={results.totalHdpe} other={results.totalSteel} otherName="steel" horizon={horizon} be={results.beVsSteel} />
              )}
              {compareAlu && results.totalAlu != null && (
                <Delta hdpe={results.totalHdpe} other={results.totalAlu} otherName="aluminum" horizon={horizon} be={results.beVsAlu} />
              )}
            </div>

            {/* Downtime callout — click-to-quote with yearly savings + days-saved */}
            {results.downtimeCallout && (
              <button
                type="button"
                onClick={() => {
                  const d = results.downtimeCallout;
                  const days = Math.round(d.days).toLocaleString();
                  const yearly = d.yearlySavings > 0 ? ` (about ${fmt$(d.yearlySavings)} a year)` : '';
                  const msg = `I ran your Lifetime Cost Calculator and an HDPE hull would save me ${days} service days over ${horizon} years vs ${d.label}${yearly}. Please send me a quote on a barge that fits my work.`;
                  if (typeof window !== 'undefined') {
                    (window.dataLayer = window.dataLayer || []).push({
                      event: 'calculator_downtime_click',
                      days_saved: d.days,
                      yearly_savings: Math.round(d.yearlySavings),
                      vs: d.label,
                      horizon,
                    });
                    window.dispatchEvent(
                      new CustomEvent('prefill-quote-message', {
                        detail: { message: msg, source: 'cost-calculator-downtime' },
                      })
                    );
                  }
                }}
                data-testid="calc-downtime-callout"
                className="mt-6 w-full text-left rounded-lg border border-cyan-400/30 bg-cyan-500/5 hover:bg-cyan-500/10 hover:border-cyan-400/60 transition-colors px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3 group"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <ArrowDown className="w-4 h-4 text-cyan-300 rotate-[-90deg] shrink-0 mt-1" />
                  <div className="min-w-0">
                    {results.downtimeCallout.yearlySavings > 0 && (
                      <div
                        className="font-display text-cyan-300 text-2xl sm:text-3xl font-bold leading-none"
                        data-testid="calc-downtime-yearly"
                      >
                        {fmt$(results.downtimeCallout.yearlySavings)}
                        <span className="text-zinc-400 text-xs sm:text-sm font-normal tracking-normal ml-1.5">
                          saved per year
                        </span>
                      </div>
                    )}
                    <div className="text-sm text-zinc-200 mt-1">
                      Plus{' '}
                      <span className="text-white font-semibold">
                        {Math.round(results.downtimeCallout.days).toLocaleString()} fewer service days
                      </span>{' '}
                      over {horizon} years vs {results.downtimeCallout.label}.
                    </div>
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 group-hover:text-cyan-200 shrink-0 pl-7 sm:pl-0">
                  Turn this into a quote →
                </span>
              </button>
            )}

            {/* Chart */}
            <CumulativeChart
              horizon={horizon}
              hdpe={results.hdpeStream}
              steel={compareSteel ? results.steelStream : null}
              aluminum={compareAlu ? results.aluStream : null}
            />

            {/* Disclaimer + CTA */}
            <p className="mt-6 text-[11px] text-zinc-300 max-w-3xl">
              Estimates are based entirely on the numbers you enter. Actual costs vary. Contact us for an exact quote.
            </p>
            <button
              type="button"
              onClick={jumpToQuote}
              data-testid="calc-cta-quote"
              className="mt-4 btn-teal px-5 sm:px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] inline-flex items-center gap-2"
            >
              Get an Exact Quote <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function isLowest(v, others) {
  if (v == null) return false;
  const vals = others.filter((x) => x != null);
  if (vals.length === 0) return false;
  return vals.every((x) => v <= x);
}

function Check({ label, checked, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-white font-medium">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-cyan-400"
        data-testid={testId}
      />
      {label}
    </label>
  );
}

function MaterialPanel({ title, tone, fields, state, setState, testId }) {
  const toneRing = tone === 'cyan'
    ? 'border-cyan-400/60 shadow-[0_0_24px_-6px_rgba(103,232,249,0.35)]'
    : tone === 'orange'
      ? 'border-orange-400/50 shadow-[0_0_24px_-6px_rgba(251,146,60,0.30)]'
      : 'border-zinc-300/40 shadow-[0_0_24px_-6px_rgba(228,228,231,0.20)]';
  const toneText = tone === 'cyan'
    ? 'text-cyan-300 drop-shadow-[0_0_8px_rgba(103,232,249,0.6)]'
    : tone === 'orange'
      ? 'text-orange-300 drop-shadow-[0_0_8px_rgba(251,146,60,0.55)]'
      : 'text-zinc-100 drop-shadow-[0_0_8px_rgba(228,228,231,0.4)]';
  const tonePrefix = tone === 'cyan' ? 'text-cyan-300' : tone === 'orange' ? 'text-orange-300' : 'text-zinc-200';
  return (
    <div className={`rounded-lg border ${toneRing} bg-black/40 p-4`} data-testid={testId}>
      <div className={`text-[11px] uppercase tracking-[0.22em] mb-3 font-bold ${toneText}`}>{title}</div>
      <div className="space-y-3">
        {fields.map(([key, label, helper, prefix]) => (
          <label key={key} className="block">
            <div className="text-[11px] text-zinc-200 font-medium mb-1">{label}</div>
            <div className="relative">
              {prefix && <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${tonePrefix} text-sm font-semibold`}>{prefix}</span>}
              <input
                type="text"
                inputMode="decimal"
                value={state[key]}
                onChange={(e) => setState({ ...state, [key]: e.target.value })}
                data-testid={`${testId}-${key}`}
                className={`w-full bg-black/70 border border-white/20 focus:border-cyan-300 focus:shadow-[0_0_0_3px_rgba(103,232,249,0.15)] outline-none rounded-md py-2.5 text-white text-sm placeholder:text-zinc-500 ${prefix ? 'pl-7 pr-3' : 'px-3'}`}
                placeholder="0"
              />
            </div>
            {helper && <div className="text-[10px] text-zinc-400 mt-1">{helper}</div>}
          </label>
        ))}
      </div>
    </div>
  );
}

function ResultCard({ label, tone, total, horizon, daysOut, revLoss, incomplete, lowest, testId }) {
  const toneBorder = tone === 'cyan'
    ? 'border-cyan-400/60 shadow-[0_0_24px_-6px_rgba(103,232,249,0.35)]'
    : tone === 'orange'
      ? 'border-orange-400/60 shadow-[0_0_24px_-6px_rgba(251,146,60,0.35)]'
      : 'border-zinc-300/50 shadow-[0_0_24px_-6px_rgba(228,228,231,0.25)]';
  const toneLabel = tone === 'cyan'
    ? 'text-cyan-300 drop-shadow-[0_0_8px_rgba(103,232,249,0.6)]'
    : tone === 'orange'
      ? 'text-orange-300 drop-shadow-[0_0_8px_rgba(251,146,60,0.55)]'
      : 'text-zinc-100 drop-shadow-[0_0_8px_rgba(228,228,231,0.4)]';
  const toneTotal = tone === 'cyan'
    ? 'text-cyan-300 drop-shadow-[0_0_14px_rgba(103,232,249,0.75)]'
    : tone === 'orange'
      ? 'text-orange-300 drop-shadow-[0_0_12px_rgba(251,146,60,0.55)]'
      : 'text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.35)]';
  const lowestExtra = lowest ? ' ring-1 ring-cyan-300/40 shadow-[0_0_40px_-8px_rgba(103,232,249,0.55)]' : '';
  return (
    <div className={`rounded-lg border ${toneBorder}${lowestExtra} bg-black/50 p-4`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[11px] uppercase tracking-[0.22em] font-bold ${toneLabel}`}>{label}</div>
        {lowest && <div className="text-[9px] uppercase tracking-[0.24em] text-cyan-300 font-bold drop-shadow-[0_0_8px_rgba(103,232,249,0.7)]">Lowest total</div>}
      </div>
      {incomplete ? (
        <div className="text-zinc-300 text-sm py-3">Enter costs to compare</div>
      ) : (
        <>
          <div className={`font-display text-2xl sm:text-3xl font-bold ${toneTotal}`}>{fmt$(total)}</div>
          <div className="text-[11px] text-zinc-300 mt-1">Total over {horizon} years</div>
          {daysOut > 0 && (
            <div className="text-[11px] text-zinc-300 mt-0.5">{Math.round(daysOut)} days out of service</div>
          )}
          {revLoss > 0 && (
            <div className="text-[11px] text-zinc-300 mt-0.5" data-testid={`${testId}-revloss`}>
              Includes <span className="text-white font-semibold">{fmt$(revLoss)}</span> revenue lost to downtime
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Delta({ hdpe, other, otherName, horizon, be }) {
  const diff = hdpe - other;
  const abs = Math.abs(diff);
  const verb = diff < 0 ? 'less than' : diff > 0 ? 'more than' : 'the same as';
  const beText = be === 'immediate'
    ? 'HDPE is cheaper from year 1.'
    : be === 'none'
      ? `HDPE does not break even against ${otherName} within 30 years.`
      : be != null
        ? `Break-even at year ${be}.`
        : '';
  return (
    <div className="text-zinc-100">
      <Calculator className="inline-block w-3.5 h-3.5 text-cyan-300 mr-1.5 -mt-0.5 drop-shadow-[0_0_6px_rgba(103,232,249,0.7)]" />
      Over {horizon} years, HDPE costs <span className="text-cyan-300 font-bold drop-shadow-[0_0_8px_rgba(103,232,249,0.6)]">{fmt$(abs)} {verb}</span> {otherName}.
      {beText && <span className="text-zinc-300"> {beText}</span>}
    </div>
  );
}

function CumulativeChart({ horizon, hdpe, steel, aluminum }) {
  const series = [
    { key: 'hdpe', data: hdpe, color: CHART_COLORS.hdpe, label: 'HDPE' },
    steel && { key: 'steel', data: steel, color: CHART_COLORS.steel, label: 'Steel' },
    aluminum && { key: 'aluminum', data: aluminum, color: CHART_COLORS.aluminum, label: 'Aluminum' },
  ].filter(Boolean);

  const w = 720, h = 260, pad = { l: 60, r: 20, t: 20, b: 30 };
  // Lift the $0 baseline off the bottom axis so a flat/near-zero HDPE line
  // is still visible instead of blending into the border.
  const bottomLift = 14;
  const xMax = horizon;
  const yMax = Math.max(1, ...series.flatMap((s) => s.data.slice(0, horizon + 1).map((d) => d.cum)));
  const xTo = (y) => pad.l + (y / xMax) * (w - pad.l - pad.r);
  const yTo = (v) => h - pad.b - bottomLift - (v / yMax) * (h - pad.t - pad.b - bottomLift);

  return (
    <div className="mt-6 panel rounded-lg border border-white/20 p-4 bg-black/60">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Cumulative cost by year</div>
        <div className="flex flex-wrap gap-4">
          {series.map((s) => (
            <div key={s.key} className="inline-flex items-center gap-1.5 text-[11px] text-white font-medium">
              <span
                className="w-3.5 h-[3px] rounded-full"
                style={{ background: s.color, boxShadow: `0 0 8px ${s.color}` }}
              />{' '}
              {s.label}
            </div>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" role="img" aria-label="Cumulative cost chart">
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line key={i} x1={pad.l} x2={w - pad.r} y1={yTo(t * yMax)} y2={yTo(t * yMax)} stroke="rgba(255,255,255,0.14)" />
        ))}
        {/* Y ticks */}
        {[0, 0.5, 1].map((t, i) => (
          <text key={i} x={pad.l - 8} y={yTo(t * yMax) + 3} fill="rgba(228,228,231,0.95)" fontSize="11" fontWeight="600" textAnchor="end">
            {fmt$(t * yMax)}
          </text>
        ))}
        {/* X ticks */}
        {Array.from({ length: horizon + 1 }).map((_, i) =>
          i % Math.max(1, Math.round(horizon / 6)) === 0 ? (
            <text key={i} x={xTo(i)} y={h - pad.b + 14} fill="rgba(228,228,231,0.9)" fontSize="11" fontWeight="600" textAnchor="middle">
              {`${i}y`}
            </text>
          ) : null
        )}
        {/* Series */}
        {series.map((s) => {
          const d = s.data.slice(0, horizon + 1).map((pt, i) => `${i === 0 ? 'M' : 'L'} ${xTo(pt.year)} ${yTo(pt.cum)}`).join(' ');
          return (
            <path
              key={s.key}
              d={d}
              fill="none"
              stroke={s.color}
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ filter: `drop-shadow(0 0 6px ${s.color})` }}
            />
          );
        })}
      </svg>
    </div>
  );
}
